import { Column, ColumnDef, createTable, getCoreRowModel } from '@tanstack/react-table';
import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { z, ZodType } from 'zod';
import {
    BoolFilterType,
    ColumnFilterType,
    EnumFilterType,
    NumberFilterType,
    Order,
    StringFilterType,
    TableFilters,
    TableState,
} from '@optimus-web/core';

const columnSortSchema = z
    .object({
        order: z.nativeEnum(Order),
        priority: z.coerce.number(),
    })
    .optional();

export const createSortSchema = <RowData>(columns: Column<RowData>[]): ZodType<TableState<RowData>['sort']> =>
    pipe(
        columns,
        array.filter((column) => column.getCanSort()),
        array.map((column) => [column.id, columnSortSchema] as const),
        Object.fromEntries<typeof columnSortSchema>,
        z.object
    );

const stringsFiltersSchema = z.array(
    z.object({
        filterType: z.nativeEnum(StringFilterType),
        value: z.string(),
    })
);

const numbersFiltersSchema = z.array(
    z.object({
        filterType: z.nativeEnum(NumberFilterType),
        value: z.coerce.number(),
    })
);

const multiSelectFiltersSchema = z.array(
    z.object({
        filterType: z.nativeEnum(EnumFilterType),
        value: z.array(z.string()),
    })
);

const boolFiltersSchema = z.array(
    z.object({
        filterType: z.nativeEnum(BoolFilterType),
        value: z.boolean(),
    })
);

const isColumnFilterType = (value: unknown): value is ColumnFilterType =>
    Object.values(ColumnFilterType).includes(value as ColumnFilterType);

type FilterSchemaType =
    | typeof stringsFiltersSchema
    | typeof numbersFiltersSchema
    | typeof multiSelectFiltersSchema
    | typeof boolFiltersSchema;

export const createFiltersSchema = <RowData, Filters extends TableFilters<RowData>>(columns: Column<RowData>[]) => {
    const filtersSchema: ZodType<TableFilters<RowData>> = pipe(
        columns,
        array.filter((column) => column.getCanFilter()),
        array.filterMap((column): option.Option<[string, FilterSchemaType]> => {
            const filtersType = column.columnDef?.meta?.filtersType;
            if (!isColumnFilterType(filtersType)) {
                return option.none;
            }
            switch (filtersType) {
                case ColumnFilterType.String:
                    return option.some([column.id, stringsFiltersSchema]);
                case ColumnFilterType.Number:
                    return option.some([column.id, numbersFiltersSchema]);
                case ColumnFilterType.MultiSelect:
                    return option.some([column.id, multiSelectFiltersSchema]);
                case ColumnFilterType.Bool:
                    return option.some([column.id, boolFiltersSchema]);
            }
        }),
        Object.fromEntries<FilterSchemaType>,
        (shape) => z.object(shape).partial()
    );
    return filtersSchema as ZodType<Partial<Filters>>;
};

export const createTableStateUrlSchema = <RowData, Filters extends TableFilters<RowData>>(
    columnDefs: ColumnDef<RowData>[]
) => {
    const table = createTable({
        columns: columnDefs,
        data: [],
        state: {},
        onStateChange: () => {},
        renderFallbackValue: null,
        getCoreRowModel: getCoreRowModel(),
        defaultColumn: {
            enableSorting: false,
            enableColumnFilter: false,
        },
    });
    const columns = table.getAllColumns();

    return z.object({
        sort: createSortSchema(columns).default({}),
        filters: createFiltersSchema<RowData, Filters>(columns).default({}),
        globalFilters: z
            .object({
                search: z.string().optional(),
            })
            .default({}),
    }) as z.ZodType<Partial<TableState<RowData, Filters>>>;
};
